import React, { useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ScanLine, Trash2, Upload } from 'lucide-react';
import { invoiceUploadError } from '../../services/invoiceUpload';

function PhotoPreview({ file, index }) {
    const [url, setUrl] = useState('');
    useEffect(() => {
        const preview = URL.createObjectURL(file);
        setUrl(preview);
        return () => URL.revokeObjectURL(preview);
    }, [file]);
    return <img src={url || undefined} alt={`Página ${index + 1}: ${file.name}`} />;
}

export default function InvoiceUpload({ onUpload, busy, compact = false }) {
    const [dragging, setDragging] = useState(false);
    const [pages, setPages] = useState([]);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const nextId = useRef(0);
    const inFlight = useRef(false);
    const disabled = busy || submitting;
    const selectFiles = selected => {
        if (disabled || !selected.length) return;
        const files = [...pages.map(page => page.file), ...selected];
        const failure = invoiceUploadError(files, pages.length > 0);
        setError(failure);
        if (failure) return;
        if (!pages.length && files.length === 1 && /\.pdf$/i.test(files[0].name)) { onUpload(files[0]); return; }
        setPages(previous => [...previous, ...selected.map(file => ({ id: ++nextId.current, file }))]);
    };
    const move = (index, delta) => setPages(previous => {
        const reordered = [...previous];
        [reordered[index], reordered[index + delta]] = [reordered[index + delta], reordered[index]];
        return reordered;
    });
    const submit = async () => {
        if (disabled || inFlight.current || !pages.length) return;
        inFlight.current = true; setSubmitting(true);
        try {
            if (await onUpload(pages.map(page => page.file))) setPages([]);
        } catch (failure) {
            setError(failure.message || 'No se ha podido leer la factura. Puedes reintentar.');
        } finally { inFlight.current = false; setSubmitting(false); }
    };
    return <div className="esave-stack">
        <label className={`esave-dropzone ${compact ? 'esave-dropzone-compact' : ''} ${dragging ? 'is-dragging' : ''}`}
            onDragOver={event => { event.preventDefault(); if (!disabled) setDragging(true); }}
            onDragLeave={() => setDragging(false)} onDrop={event => {
                event.preventDefault(); setDragging(false); selectFiles(Array.from(event.dataTransfer.files));
            }}>
            <span className="esave-upload-icon"><Upload size={26} aria-hidden="true" /></span>
            <strong>{pages.length ? 'Añade más fotos de la misma factura' : 'Arrastra tu factura o las fotos de sus páginas aquí'}</strong>
            <span className="esave-button">{pages.length ? 'Añadir fotos' : 'Seleccionar archivos'}</span>
            <span className="esave-muted">{pages.length ? 'Hasta 10 fotos' : 'Un PDF o hasta 10 fotos'} JPG, JPEG o PNG · Hasta 16 MB en total</span>
            <input type="file" multiple className="sr-only" aria-label="Subir y leer factura" accept={pages.length ? '.jpg,.jpeg,.png' : '.pdf,.jpg,.jpeg,.png'} disabled={disabled}
                onChange={event => { const files = Array.from(event.target.files || []); event.target.value = ''; selectFiles(files); }} />
        </label>
        {error && <p className="esave-field-error" role="alert">{error}</p>}
        {pages.length > 0 && <div className="esave-photo-selection">
            <div><h4>Fotos de la factura · {pages.length} {pages.length === 1 ? 'página' : 'páginas'}</h4>
                <p className="esave-muted">Ordena las páginas antes de leerlas. Se guardará un único PDF con estas fotos.</p></div>
            <ol className="esave-photo-list" aria-label="Páginas de la factura">{pages.map((page, index) => <li key={page.id}>
                <PhotoPreview file={page.file} index={index} />
                <div className="esave-photo-name"><strong>Página {index + 1}</strong><span className="esave-caption">{page.file.name}</span></div>
                <div className="esave-row-actions">
                    <button type="button" className="esave-button" disabled={disabled || index === 0} aria-label={`Subir página ${index + 1}`} onClick={() => move(index, -1)}><ArrowUp size={15} aria-hidden="true" /></button>
                    <button type="button" className="esave-button" disabled={disabled || index === pages.length - 1} aria-label={`Bajar página ${index + 1}`} onClick={() => move(index, 1)}><ArrowDown size={15} aria-hidden="true" /></button>
                    <button type="button" className="esave-button esave-button-danger" disabled={disabled} aria-label={`Quitar página ${index + 1}`} onClick={() => { setPages(previous => previous.filter(item => item.id !== page.id)); setError(''); }}><Trash2 size={15} aria-hidden="true" /></button>
                </div>
            </li>)}</ol>
            <div className="esave-row-actions esave-photo-actions">
                <button type="button" className="esave-button" disabled={disabled} onClick={() => { setPages([]); setError(''); }}>Quitar todas</button>
                <button type="button" className="esave-button esave-button-primary" disabled={disabled} onClick={submit}><ScanLine size={16} aria-hidden="true" />{submitting ? 'Leyendo factura…' : 'Leer factura'}</button>
            </div>
        </div>}
    </div>;
}
